<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-xl font-bold text-slate-800">Integration</h3>
    </div>

    <section class="overflow-hidden">
      <div
        class="grid grid-cols-5 gap-4 px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
      >
        <span>Integration</span>
        <span>Status</span>
        <span>Date</span>
        <span>Shared User</span>
        <span class="text-right">Action</span>
      </div>

      <div class="divide-y divide-slate-100">
        <div
          v-if="integrations.length === 0"
          class="text-center text-sm text-slate-500 mt-20"
        >
          No integrations found.
        </div>
        <div
          v-else
          v-for="integration in integrations"
          :key="integration.id"
          class="relative grid grid-cols-5 items-center gap-4 px-6 py-4 mb-2 border border-slate-100 rounded-lg bg-white"
        >
          <span
            class="absolute left-0 top-0 h-full w-1 rounded-l bg-blue-400"
          ></span>

          <div
            class="flex items-center gap-3 text-sm font-semibold text-slate-800"
          >
            <span>{{ integration.name }}</span>
          </div>
          <div class="flex items-center gap-2 text-sm">
            <span :class="statusDotClass(integration.status)"></span>
            <span :class="statusTextClass(integration.status)">
              {{ statusLabel(integration.status) }}
            </span>
          </div>
          <div class="text-sm text-slate-600">
            {{ integration.date }}
          </div>
          <div class="text-sm text-slate-600">
            {{ integration.sharedUsers }}
          </div>
          <div class="text-right">
            <button
              v-if="integration.status === 'integrated'"
              type="button"
              class="text-sm font-semibold text-blue-600 transition hover:text-blue-700 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
              @click="openDrawer(integration)"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </section>

    <Drawer
      :is-drawer-open="isDrawerOpen"
      title=""
      :is-right-drawer="true"
      drawer-width="w-full md:w-2/5"
      drawer-padding="p-0"
      @close-drawer="closeDrawer"
    >
      <template #main-content>
        <IntegrationDetail
          v-if="selectedIntegration"
          :integration="selectedIntegration"
          @close="closeDrawer"
        />
      </template>
    </Drawer>
  </div>
</template>

<script>
import Drawer from "../core/Drawer.vue";
import IntegrationDetail from "../Forms/IntegrationDetail.vue";
import AnalyticsIntegration from "../../apis/AnalyticsIntegration";

export default {
  name: "IntegrationTab",
  components: {
    Drawer,
    IntegrationDetail
  },
  data() {
    return {
      integrations: [],
      loading: false,
      error: null,
      isDrawerOpen: false,
      selectedIntegration: null
    };
  },
  props: {
    tenantId: {
      type: [String, Number],
      required: true
    }
  },
  computed: {
    hasIntegrations() {
      return this.integrations.length > 0;
    }
  },
  watch: {
    tenantId: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          return;
        }
        this.fetchIntegrations();
      }
    }
  },
  methods: {
    async fetchIntegrations() {
      this.loading = true;
      this.error = null;
      try {
        const response = await AnalyticsIntegration.getTenantIntegrations(this.tenantId);
        const payload = response?.data ?? response ?? {};
        this.integrations = this.mapIntegrations(payload);
      } catch (error) {
        this.error = "Failed to load integrations";
        this.integrations = [];
      } finally {
        this.loading = false;
      }
    },
    mapIntegrations(payload) {
      if (!payload || typeof payload !== "object") {
        return [];
      }

      return Object.entries(payload).map(([key, value], index) => {
        const normalized = this.normalizeIntegration(key, value);
        return {
          id: `${key}-${index}`,
          name: normalized.label,
          status: normalized.status,
          date: normalized.lastSynced,
          sharedUsers: normalized.sharedUsers,
          details: normalized.details
        };
      });
    },
    normalizeIntegration(key, value = {}) {
      const integrationKey = key.toString();
      const normalizedKey = integrationKey
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      const statusRaw = (value.status || value.integration_status || "Not Integrated").toString();
      const statusLower = statusRaw.toLowerCase();
      const hasPositiveStatus =
        statusLower.includes("integrated") ||
        statusLower.includes("active") ||
        statusLower.includes("connected");
      let normalizedStatus = "not-integrated";

      if (hasPositiveStatus && !statusLower.includes("not")) {
        normalizedStatus = "integrated";
      }
      const integrationCount = value.integration_count ?? value.count;
      const partnerCount = value.partner_count;
      const activeCount = value.active_integration_count;
      const inactiveCount = value.inactive_integration_count;
      const connectedForms = value.connected_forms_count;

      const integrationUsers =
        value.integration_user_count ?? value.user_count ?? value.shared_users;
      const firstIntegrated = value.first_integrated_at || value.first_connected_at;
      const lastSynced =
        value.last_synced_at ||
        value.updated_at ||
        value.created_at ||
        firstIntegrated;

      const details = [];

      details.push({
        label: "Integration Status",
        value: normalizedStatus === "integrated" ? "Integrated" : "Not Integrated",
        variant: "status",
        statusClass: normalizedStatus === "integrated" ? "bg-green-100 text-green-600" : "bg-red-200 text-red-600",
        dotClass: normalizedStatus === "integrated" ? "bg-green-500" : "bg-red-500",
        icon: "Connector"
      });

      if (integrationCount != null) {
        details.push({
          label: "Integrations",
          value: integrationCount,
          icon: "ManualIcon"
        });
      }

      if (partnerCount != null) {
        details.push({
          label: "Connected Partners",
          value: partnerCount,
          icon: "Partners"
        });
      }

      if (activeCount != null || inactiveCount != null) {
        details.push({
          label: "Active Connection",
          value: activeCount ?? 0,
          icon: "ActiveConnection"
        });
        details.push({
          label: "Inactive Connection",
          value: inactiveCount ?? 0,
          icon: "InactiveConnection"
        });
      }

      if (connectedForms != null) {
        details.push({
          label: "Connected Forms",
          value: connectedForms,
          icon: "ConnectedForms"
        });
      }

      if (integrationUsers != null) {
        details.push({
          label: "Integration Users",
          value: integrationUsers,
          icon: "Users"
        });
      }

      if (
        integrationCount === 0 &&
        partnerCount === undefined &&
        activeCount === undefined &&
        inactiveCount === undefined &&
        connectedForms === undefined &&
        integrationUsers === undefined
      ) {
        details.push({
          label: "Details",
          value: "No additional information available",
          icon: "Info"
        });
      }

      return {
        key: integrationKey,
        label: normalizedKey,
        status: normalizedStatus,
        lastSynced: lastSynced || "-",
        sharedUsers: integrationUsers != null && integrationUsers !== ""
          ? `${integrationUsers} Users`
          : "-",
        details
      };
    },
    statusLabel(status) {
      return status === "integrated" ? "Integrated" : "Not Integrated";
    },
    statusDotClass(status) {
      return [
        "inline-block h-2 w-2 rounded-full",
        status === "integrated" ? "bg-blue-500" : "bg-gray-500"
      ];
    },
    statusTextClass(status) {
      return status === "integrated" ? "text-blue-600" : "text-gray-500";
    },
    accentClass(status) {
      return status === "integrated" ? "bg-blue-400" : "bg-gray-500";
    },
    openDrawer(integration) {
      this.selectedIntegration = integration;
      this.isDrawerOpen = true;
    },
    closeDrawer() {
      this.isDrawerOpen = false;
      setTimeout(() => {
        this.selectedIntegration = null;
      }, 200);
    }
  }
};
</script>
